import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Table,
  ProgressBar,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  Store,
  ShoppingCart,
  Package,
  Users,
  BarChart3,
  TrendingUp,
  DollarSign,
  AlertTriangle,
  Calendar,
  Activity,
  RefreshCw,
  ArrowRight,
  Eye,
  Plus,
  CreditCard,
  Warehouse,
  ShoppingBag,
  UserCheck,
  Target,
  Clock,
} from "lucide-react";

// Services
import { statsService } from "../../services/statsService";
import LoadingSpinner from "../common/LoadingSpinner";
import { useBoutique } from "../../contexts/BoutiqueContext";
import { canShowCredits } from "../../utils/creditHelper";

const ManagerDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const { currentBoutique } = useBoutique();

  // États pour les données du dashboard
  const [dashboardData, setDashboardData] = useState({
    todayStats: {
      sale_count: 0,
      total_revenue: 0,
      average_sale: 0,
      growth_rate: 0,
    },
    topProducts: [],
    stockAlerts: [],
    creditSales: [],
    teamActivity: [],
  });

  // Charger les données au montage
  useEffect(() => {
    loadDashboard();
  }, []);

  /**
   * Calculer les statistiques du jour depuis les ventes
   */
  const calculateTodayStats = (salesData) => {
    const today = new Date();
    const todayStr = today.toISOString().split("T")[0];

    console.log("📊 Calcul des stats du jour pour:", todayStr);
    console.log("📋 Données de ventes:", salesData.length);

    // ✅ FILTRAGE : Exclure les ventes annulées
    const activeSalesData = salesData.filter((sale) => {
      const isCancelled =
        sale.isCancelled || sale.is_cancelled || sale.status === "CANCELLED";
      return !isCancelled;
    });

    console.log("📋 Ventes actives (non annulées):", activeSalesData.length);
    console.log(
      "❌ Ventes annulées exclues:",
      salesData.length - activeSalesData.length
    );

    // ✅ ÉTAPE 1 : Ventes du jour (nouvelles ventes créées aujourd'hui)
    const todaySales = activeSalesData.filter((sale) => {
      const saleDate = new Date(
        sale.saleDate || sale.createdAt || sale.created_at || sale.date
      );
      const isToday = saleDate.toISOString().split("T")[0] === todayStr;

      if (isToday) {
        console.log("✅ Vente du jour trouvée:", {
          id: sale.id,
          total: sale.total,
          paymentMethod: sale.paymentMethod || sale.payment_method,
          isCredit: sale.isCredit || sale.is_credit,
          date: saleDate.toLocaleDateString(),
        });
      }

      return isToday;
    });

    // ✅ ÉTAPE 2 : Revenus des ventes du jour
    const todaySalesRevenue = todaySales.reduce(
      (sum, sale) => sum + (sale.total || 0),
      0
    );

    // ✅ ÉTAPE 3 : Paiements de crédits effectués aujourd'hui
    // Chercher les ventes à crédit qui ont reçu des paiements aujourd'hui
    const todayCreditPayments = activeSalesData.filter((sale) => {
      // Vérifier si c'est une vente à crédit
      const isCredit =
        sale.paymentMethod === "CREDIT" ||
        sale.payment_method === "CREDIT" ||
        sale.isCredit ||
        sale.is_credit;

      if (!isCredit) return false;

      // Vérifier si il y a eu un paiement aujourd'hui
      // Si la vente a un champ de date de paiement, l'utiliser
      const paymentDate =
        sale.creditPaymentDate ||
        sale.credit_payment_date ||
        sale.lastPaymentDate ||
        sale.last_payment_date ||
        sale.updatedAt ||
        sale.updated_at;

      if (paymentDate) {
        const paymentDateObj = new Date(paymentDate);
        const isPaymentToday =
          paymentDateObj.toISOString().split("T")[0] === todayStr;

        // Vérifier aussi si le crédit a été soldé (payé entièrement)
        const isCreditPaid =
          sale.isCreditPaid ||
          sale.is_credit_paid ||
          (sale.creditPaid && sale.creditPaid >= sale.total) ||
          (sale.credit_paid && sale.credit_paid >= sale.total);

        if (isPaymentToday && isCreditPaid) {
          console.log("💳 Paiement de crédit du jour trouvé:", {
            saleId: sale.id,
            originalSaleDate: new Date(
              sale.saleDate || sale.createdAt || sale.created_at
            ).toLocaleDateString(),
            paymentDate: paymentDateObj.toLocaleDateString(),
            total: sale.total,
            creditPaid: sale.creditPaid || sale.credit_paid,
          });
          return true;
        }
      }

      return false;
    });

    // ✅ ÉTAPE 4 : Revenus des paiements de crédits du jour
    const todayCreditPaymentsRevenue = todayCreditPayments.reduce(
      (sum, sale) => {
        // Prendre le montant payé aujourd'hui (ou le total si entièrement payé)
        const creditPaid = sale.creditPaid || sale.credit_paid || 0;
        const total = sale.total || 0;

        // Si le crédit est entièrement payé aujourd'hui, compter le total
        // Sinon, il faudrait avoir le montant spécifique payé aujourd'hui
        return sum + Math.min(creditPaid, total);
      },
      0
    );

    // ✅ ÉTAPE 5 : CHIFFRE D'AFFAIRES TOTAL = Ventes du jour + Paiements crédits du jour
    const totalTodayRevenue = todaySalesRevenue + todayCreditPaymentsRevenue;

    // ✅ ÉTAPE 6 : Nombre total de transactions (ventes + paiements crédits)
    const totalTransactions = todaySales.length + todayCreditPayments.length;

    // ✅ ÉTAPE 7 : Moyenne par transaction
    const averageSale =
      totalTransactions > 0 ? totalTodayRevenue / totalTransactions : 0;

    console.log("📊 === RÉSUMÉ CHIFFRE D'AFFAIRES DU JOUR ===");
    console.log(
      "💰 Ventes du jour:",
      todaySales.length,
      "pour",
      todaySalesRevenue,
      "FCFA"
    );
    console.log(
      "💳 Paiements crédits du jour:",
      todayCreditPayments.length,
      "pour",
      todayCreditPaymentsRevenue,
      "FCFA"
    );
    console.log("🎯 CHIFFRE D'AFFAIRES TOTAL:", totalTodayRevenue, "FCFA");
    console.log("📈 Moyenne par transaction:", averageSale, "FCFA");

    // ✅ RETOUR avec les mêmes noms de champs que l'original
    return {
      sale_count: totalTransactions, // Nombre total de transactions
      total_revenue: totalTodayRevenue, // CHIFFRE D'AFFAIRES TOTAL (ventes + crédits payés)
      average_sale: averageSale,
      growth_rate: 5.2, // Simulation - en réalité, calculer vs hier

      // ✅ NOUVEAUX CHAMPS pour plus de détails (optionnels)
      today_sales_count: todaySales.length,
      today_sales_revenue: todaySalesRevenue,
      today_credit_payments_count: todayCreditPayments.length,
      today_credit_payments_revenue: todayCreditPaymentsRevenue,
    };
  };

  /**
   * Calculer les produits les plus vendus
   */
  const calculateTopProducts = (salesData) => {
    // ✅ FILTRAGE : Exclure les ventes annulées
    const activeSalesData = salesData.filter((sale) => {
      const isCancelled =
        sale.isCancelled || sale.is_cancelled || sale.status === "CANCELLED";
      return !isCancelled;
    });

    console.log(
      "📦 Calcul top produits - Ventes actives:",
      activeSalesData.length
    );

    const productStats = {};

    activeSalesData.forEach((sale) => {
      if (sale.products) {
        sale.products.forEach((product) => {
          const productId = product.id || product.productId;
          const productName =
            product.name || product.productName || "Produit inconnu";
          const quantity = product.quantity || 1;

          if (!productStats[productId]) {
            productStats[productId] = {
              id: productId,
              name: productName,
              totalSold: 0,
              revenue: 0,
            };
          }

          productStats[productId].totalSold += quantity;
          productStats[productId].revenue += (product.price || 0) * quantity;
        });
      }
    });

    return Object.values(productStats)
      .sort((a, b) => b.totalSold - a.totalSold)
      .slice(0, 5)
      .map((product, index) => ({
        ...product,
        rank: index + 1,
      }));
  };

  /**
   * Calculer les alertes de stock
   */
  const calculateStockAlerts = (stockData) => {
    return stockData
      .filter((item) => item.quantity <= (item.minStock || 10))
      .map((item) => ({
        ...item,
        urgency:
          item.quantity === 0 ? "high" : item.quantity <= 5 ? "high" : "medium",
      }))
      .slice(0, 5);
  };

  /**
   * Calculer les 5 dernières ventes à crédit
   */
  const calculateCreditSales = (salesData) => {
    const today = new Date();
    const todayStr = today.toISOString().split("T")[0];

    // ✅ FILTRAGE : Exclure les ventes annulées
    const activeSalesData = salesData.filter((sale) => {
      const isCancelled =
        sale.isCancelled || sale.is_cancelled || sale.status === "CANCELLED";
      return !isCancelled;
    });

    console.log(
      "💳 Calcul ventes crédit - Ventes actives:",
      activeSalesData.length
    );

    const creditSales = activeSalesData.filter((sale) => {
      const isCredit =
        sale.paymentMethod === "CREDIT" ||
        sale.payment_method === "CREDIT" ||
        sale.isCredit ||
        sale.is_credit;

      const isCreditPaid =
        sale.isCreditPaid ||
        sale.is_credit_paid ||
        (sale.creditPaid && sale.creditPaid >= sale.total) ||
        (sale.credit_paid && sale.credit_paid >= sale.total);

      // Inclure les crédits en cours ET ceux payés aujourd'hui
      if (isCredit && !isCreditPaid) {
        return true; // Crédit en cours
      }

      // Crédit payé aujourd'hui
      if (isCredit && isCreditPaid) {
        const paymentDate =
          sale.creditPaymentDate ||
          sale.credit_payment_date ||
          sale.lastPaymentDate ||
          sale.last_payment_date ||
          sale.updatedAt ||
          sale.updated_at;

        if (paymentDate) {
          const paymentDateObj = new Date(paymentDate);
          return paymentDateObj.toISOString().split("T")[0] === todayStr;
        }
      }

      return false;
    });

    return creditSales
      .map((sale) => ({
        ...sale,
        // Ajouter un flag pour distinguer les types
        creditStatus: (() => {
          const isCreditPaid = sale.isCreditPaid || sale.is_credit_paid;
          const paymentDate =
            sale.creditPaymentDate || sale.credit_payment_date;

          if (isCreditPaid && paymentDate) {
            const paymentDateObj = new Date(paymentDate);
            const isPaymentToday =
              paymentDateObj.toISOString().split("T")[0] === todayStr;

            if (isPaymentToday) {
              return "PAID_TODAY"; // Payé aujourd'hui
            } else {
              return "PAID_BEFORE"; // Payé avant
            }
          }

          return "PENDING"; // En cours
        })(),
      }))
      .slice(0, 10); // Augmenter la limite pour voir plus de crédits
  };

  const formatAmountWithDetail = (amount, details = null) => {
    const formatted = new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
      minimumFractionDigits: 0,
    }).format(amount || 0);

    if (details) {
      return `${formatted} ${details}`;
    }

    return formatted;
  };

  /**
   * Calculer l'activité de l'équipe
   */
  const calculateTeamActivity = (salesData) => {
    // ✅ FILTRAGE : Exclure les ventes annulées
    const activeSalesData = salesData.filter((sale) => {
      const isCancelled =
        sale.isCancelled || sale.is_cancelled || sale.status === "CANCELLED";
      return !isCancelled;
    });

    console.log(
      "👥 Calcul activité équipe - Ventes actives:",
      activeSalesData.length
    );

    const teamStats = {};

    activeSalesData.forEach((sale) => {
      const sellerId =
        sale.seller?.id || sale.sellerId || sale.userId || "unknown";
      const sellerName =
        (sale.seller
          ? `${sale.seller.name.trim() || sale.seller.nom.trim() || ""}`
          : null) || `Vendeur ${sellerId}`;

      if (!teamStats[sellerId]) {
        teamStats[sellerId] = {
          id: sellerId,
          name: sellerName,
          salesCount: 0,
          revenue: 0,
        };
      }

      teamStats[sellerId].salesCount += 1;
      teamStats[sellerId].revenue += sale.total || 0;
    });

    return Object.values(teamStats)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
  };

  /**
   * ✅ Charger les données du dashboard depuis les endpoints serveur agrégés
   * PLUS de saleService.getSales() → tout est calculé côté serveur
   */
  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("🔄 Chargement du dashboard manager (endpoints serveur)...");

      // ✅ 2 appels serveur seulement (au lieu de 4 dont getSales lourd)
      const [dashboardResponse, teamResponse] = await Promise.allSettled([
        statsService.getDashboard(),
        statsService.getTeamPerformance(),
      ]);

      // ========== DASHBOARD PRINCIPAL ==========
      let rawData = null;
      if (
        dashboardResponse.status === "fulfilled" &&
        dashboardResponse.value?.success
      ) {
        rawData = dashboardResponse.value.data;
        console.log("✅ Dashboard brut:", JSON.stringify(rawData, null, 2));
      } else {
        console.warn("⚠️ Dashboard échoué:", dashboardResponse.reason || dashboardResponse.value);
      }

      // ========== TEAM PERFORMANCE ==========
      let teamData = null;
      if (
        teamResponse.status === "fulfilled" &&
        teamResponse.value?.success
      ) {
        teamData = teamResponse.value.data;
        console.log("✅ Team brut:", teamData);
      } else {
        console.warn("⚠️ Team échoué (endpoint peut-être pas encore déployé)");
      }

      // ========== ASSEMBLAGE avec mapping snake_case → format attendu ==========
      
      // --- todayStats ---
      const rawStats = rawData?.today_stats || rawData?.todayStats || null;
      console.log("📊 Raw todayStats:", rawStats);
      const todayStats = {
        sale_count: rawStats?.sale_count ?? rawStats?.saleCount ?? 0,
        total_revenue: rawStats?.total_revenue ?? rawStats?.totalRevenue ?? 0,
        average_sale: rawStats?.average_sale ?? rawStats?.averageSale ?? 0,
        growth_rate: rawStats?.growth_rate ?? rawStats?.growthRate ?? 0,
      };

      // --- topProducts ---
      const rawProducts = rawData?.top_products || rawData?.topProducts || [];
      console.log("📦 Raw topProducts:", rawProducts?.length);

      // --- stockAlerts ---
      const rawAlerts = rawData?.stock_alerts || rawData?.stockAlerts || [];
      console.log("⚠️ Raw stockAlerts:", rawAlerts?.length);
      const stockAlerts = rawAlerts.map(alert => ({
        ...alert,
        // Le JSX utilise alert.quantity et alert.productName
        quantity: alert.quantity ?? alert.currentStock ?? 0,
        productName: alert.productName ?? alert.product_name ?? "Produit",
        urgency: (alert.quantity ?? alert.currentStock ?? 0) === 0 ? "high" 
                 : (alert.quantity ?? alert.currentStock ?? 0) <= 5 ? "high" : "medium",
      }));

      // --- creditSales ---
      const rawCredits = rawData?.credit_sales || rawData?.creditSales || [];
      console.log("💳 Raw creditSales:", rawCredits?.length);
      const creditSales = rawCredits.map(sale => ({
        ...sale,
        // ✅ Le JSX utilise sale.customer.name — mais le backend envoie customer_name (flat)
        customer: sale.customer || { 
          name: sale.customer_name || sale.customerName || "Client" 
        },
        // ✅ Le JSX utilise sale.createdAt — le backend envoie created_at
        createdAt: sale.createdAt || sale.created_at || sale.saleDate,
        // ✅ Le JSX utilise sale.saleNumber — le backend envoie sale_number
        saleNumber: sale.saleNumber || sale.sale_number,
      }));

      // --- teamActivity ---
      const teamActivity = (teamData?.sellers || []).map((seller, index) => ({
        id: index,
        name: seller.name || "Vendeur",
        salesCount: seller.salesToday || 0,
        revenue: seller.revenueToday || 0,
      }));

      const combinedDashboardData = {
        todayStats,
        topProducts: rawProducts,
        stockAlerts,
        creditSales,
        teamActivity,
      };

      console.log("🎯 Dashboard final assemblé:", combinedDashboardData);

      setDashboardData(combinedDashboardData);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("❌ Erreur critique dashboard:", error);
      setError("Erreur de connexion au serveur");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Rafraîchir les données
   */
  const handleRefresh = () => {
    loadDashboard();
  };

  /**
   * Formater un montant
   */
  const formatAmount = (amount) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  /**
   * Obtenir la couleur du badge selon le niveau d'urgence
   */
  const getUrgencyBadge = (level) => {
    switch (level) {
      case "high":
        return "danger";
      case "medium":
        return "warning";
      case "low":
        return "info";
      default:
        return "secondary";
    }
  };

  // Navigation vers les différentes pages
  const navigateToSales = () => navigate("/sales");
  const navigateToProducts = () => navigate("/products");
  const navigateToStock = () => navigate("/stock");
  const navigateToCustomers = () => navigate("/customers");
  const navigateToReports = () => navigate("/analytics");
  const navigateToPOS = () => navigate("/pos");

  if (loading) {
    return (
      <Container fluid className="py-4">
        <LoadingSpinner message="Chargement du dashboard..." />
      </Container>
    );
  }

  return (
    <Container fluid className="dashboard-container">
      {/* En-tête du dashboard */}
      <div className="dashboard-header">
        <Row className="align-items-center">
          <Col xs={12} lg={8}>
            <div className="header-content">
              <Store className="header-icon" size={32} />
              <div className="header-text">
                <h1 className="dashboard-title">Dashboard Gestionnaire</h1>
                <p className="dashboard-subtitle">
                  Vue d'ensemble de votre boutique
                  {lastUpdated && (
                    <span className="ms-2 text-muted">
                      • Mis à jour {lastUpdated.toLocaleTimeString()}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </Col>
          <Col xs={12} lg={4}>
            <div className="header-actions">
              <Button
                variant="outline-primary"
                onClick={handleRefresh}
                className="refresh-btn"
                disabled={loading}
              >
                <RefreshCw size={16} className={loading ? "spinning" : ""} />
                <span className="d-none d-sm-inline ms-2">Actualiser</span>
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      {error && (
        <Row className="mb-4">
          <Col>
            <div className="alert alert-danger">
              <AlertTriangle size={20} className="me-2" />
              {error}
            </div>
          </Col>
        </Row>
      )}

      {/* Statistiques principales */}
      <Row className="stats-grid mb-4">
        <Col xs={6} lg={3} className="mb-3">
          <Card className="stat-card h-100">
            <Card.Body className="text-center">
              <div className="stat-icon-wrapper bg-primary">
                <ShoppingCart className="stat-icon" size={24} />
              </div>
              <div className="stat-value">
                {dashboardData.todayStats.sale_count}
              </div>
              <div className="stat-label">Ventes aujourd'hui</div>
              {dashboardData.todayStats.growth_rate !== 0 && (
                <div
                  className={`stat-growth ${
                    dashboardData.todayStats.growth_rate > 0
                      ? "positive"
                      : "negative"
                  }`}
                >
                  <TrendingUp size={14} />
                  {Math.abs(dashboardData.todayStats.growth_rate).toFixed(1)}%
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col xs={6} lg={3} className="mb-3">
          <Card className="stat-card h-100">
            <Card.Body className="text-center">
              <div className="stat-icon-wrapper bg-success">
                <DollarSign className="stat-icon" size={24} />
              </div>
              <div className="stat-value">
                {formatAmount(dashboardData.todayStats.total_revenue)}
              </div>
              <div className="stat-label">Chiffre d'affaires</div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={6} lg={3} className="mb-3">
          <Card className="stat-card h-100">
            <Card.Body className="text-center">
              <div className="stat-icon-wrapper bg-info">
                <Package className="stat-icon" size={24} />
              </div>
              <div className="stat-value">
                {dashboardData.topProducts.length}
              </div>
              <div className="stat-label">Produits actifs</div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={6} lg={3} className="mb-3">
          <Card className="stat-card h-100">
            <Card.Body className="text-center">
              <div className="stat-icon-wrapper bg-warning">
                <AlertTriangle className="stat-icon" size={24} />
              </div>
              <div className="stat-value">
                {dashboardData.stockAlerts.length}
              </div>
              <div className="stat-label">Alertes stock</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Actions rapides */}
      <Row className="quick-actions mb-4">
        <Col xs={6} md={4} lg={2} className="mb-3">
          <Card className="action-card h-100" onClick={navigateToPOS}>
            <Card.Body className="text-center">
              <div className="action-icon bg-primary">
                <ShoppingBag size={32} />
              </div>
              <div className="action-label">Nouvelle Vente</div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={6} md={4} lg={2} className="mb-3">
          <Card className="action-card h-100" onClick={navigateToProducts}>
            <Card.Body className="text-center">
              <div className="action-icon bg-info">
                <Package size={32} />
              </div>
              <div className="action-label">Produits</div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={6} md={4} lg={2} className="mb-3">
          <Card className="action-card h-100" onClick={navigateToStock}>
            <Card.Body className="text-center">
              <div className="action-icon bg-warning">
                <Warehouse size={32} />
              </div>
              <div className="action-label">Stock</div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={6} md={4} lg={2} className="mb-3">
          <Card className="action-card h-100" onClick={navigateToSales}>
            <Card.Body className="text-center">
              <div className="action-icon bg-success">
                <BarChart3 size={32} />
              </div>
              <div className="action-label">Ventes</div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={6} md={4} lg={2} className="mb-3">
          <Card className="action-card h-100" onClick={navigateToCustomers}>
            <Card.Body className="text-center">
              <div className="action-icon bg-secondary">
                <Users size={32} />
              </div>
              <div className="action-label">Clients</div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={6} md={4} lg={2} className="mb-3">
          <Card className="action-card h-100" onClick={navigateToReports}>
            <Card.Body className="text-center">
              <div className="action-icon bg-dark">
                <Activity size={32} />
              </div>
              <div className="action-label">Rapports</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Contenu principal en grille responsive */}
      <Row>
        {/* Top Produits */}
        <Col lg={6} className="mb-4">
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-0 py-3">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <Package className="me-2" size={20} />
                  Top Produits
                </h5>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={navigateToProducts}
                >
                  Voir tout <ArrowRight size={14} className="ms-1" />
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {dashboardData.topProducts &&
              dashboardData.topProducts.length > 0 ? (
                <div className="top-products">
                  {dashboardData.topProducts
                    .slice(0, 5)
                    .map((product, index) => (
                      <div
                        key={product.product_id || product.id || index}
                        className="product-item d-flex justify-content-between align-items-center mb-3"
                      >
                        <div className="d-flex align-items-center">
                          <span className="product-rank">{index + 1}</span>
                          <div>
                            <strong>
                              {product.product_name ||
                                product.productName ||
                                "Produit"}
                            </strong>
                            <div className="text-muted small">
                              {product.sale_count || product.quantity || 0}{" "}
                              vendus
                            </div>
                          </div>
                        </div>
                        <div className="text-end">
                          <div className="text-success fw-bold">
                            {formatAmount(
                              product.total_revenue || product.totalRevenue || 0
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  <Package size={32} className="mb-2" />
                  <p>Aucune vente aujourd'hui</p>
                  <small>Les produits les plus vendus apparaîtront ici</small>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Alertes Stock */}
        <Col lg={6} className="mb-4">
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-0 py-3">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <AlertTriangle className="me-2" size={20} />
                  Alertes Stock
                </h5>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={navigateToStock}
                >
                  Gérer stock <ArrowRight size={14} className="ms-1" />
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {dashboardData.stockAlerts &&
              dashboardData.stockAlerts.length > 0 ? (
                <div className="stock-alerts">
                  {dashboardData.stockAlerts.slice(0, 5).map((alert, index) => (
                    <div
                      key={alert.productId || alert.id || index}
                      className="alert-item d-flex justify-content-between align-items-center mb-3"
                    >
                      <div>
                        <strong>
                          {alert.productName || alert.name || "Produit"}
                        </strong>
                        <div className="text-muted small">
                          Stock: {alert.currentStock || alert.quantity || 0} /
                          Seuil: {alert.threshold || alert.minThreshold || 0}
                        </div>
                      </div>
                      <Badge bg={getUrgencyBadge(alert.urgency)}>
                        {alert.urgency === "high" ? "Critique" : "Attention"}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  <AlertTriangle size={32} className="mb-2" />
                  <p>Aucune alerte stock</p>
                  <small>Tous vos produits sont bien approvisionnés</small>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Ventes à crédit et Activité équipe */}
      <Row>
        {/* Ventes à crédit */}
        {canShowCredits(currentBoutique) && (
          <Col lg={6} className="mb-4">
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-0 py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <Clock className="me-2" size={20} />
                    Crédit en cours
                  </h5>
                  <Button
                    variant="outline-warning"
                    size="sm"
                    onClick={navigateToSales}
                  >
                    Voir tout <ArrowRight size={14} className="ms-1" />
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                {dashboardData.creditSales &&
                dashboardData.creditSales.length > 0 ? (
                  <div className="credit-sales">
                    {dashboardData.creditSales
                      .slice(0, 5)
                      .map((sale, index) => (
                        <div
                          key={sale.id || index}
                          className="credit-item d-flex justify-content-between align-items-center mb-3"
                        >
                          <div>
                            <strong>
                              {sale.customer?.name ||
                                sale.customer_name ||
                                sale.customerName ||
                                `Vente #${sale.saleNumber || sale.sale_number || index + 1}`}
                            </strong>
                            <div className="text-muted small">
                              {new Date(
                                sale.createdAt || sale.created_at || sale.saleDate || Date.now()
                              ).toLocaleDateString("fr-FR")}
                            </div>
                          </div>
                          <div className="text-end">
                            <div className="text-warning fw-bold">
                              {formatAmount(sale.total || 0)}
                            </div>
                            <Badge bg="warning" className="small">
                              En attente
                            </Badge>
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="text-center text-muted py-4">
                    <Clock size={32} className="mb-2" />
                    <p>Aucune vente à crédit</p>
                    <small>
                      Les ventes en attente de paiement apparaîtront ici
                    </small>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}

        {/* Activité de l'équipe */}
        <Col lg={6} className="mb-4">
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-0 py-3">
              <h5 className="mb-0">
                <Users className="me-2" size={20} />
                Activité de l'équipe
              </h5>
            </Card.Header>
            <Card.Body>
              {dashboardData.teamActivity &&
              dashboardData.teamActivity.length > 0 ? (
                <div className="team-activity">
                  {dashboardData.teamActivity
                    .slice(0, 5)
                    .map((member, index) => (
                      <div
                        key={member.id || index}
                        className="team-member d-flex justify-content-between align-items-center mb-3"
                      >
                        <div>
                          <strong>{member.name || "Employé"}</strong>
                          <div className="text-muted small">
                            {member.salesCount || 0} ventes aujourd'hui
                          </div>
                        </div>
                        <div className="text-end">
                          <div className="text-success fw-bold">
                            {formatAmount(member.revenue || 0)}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  <Users size={32} className="mb-2" />
                  <p>Aucune activité aujourd'hui</p>
                  <small>
                    Les données apparaîtront après les premières ventes
                  </small>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Styles CSS intégrés */}
      <style jsx>{`
        .dashboard-container {
          padding: 1.5rem;
          background: #f8f9fa;
          min-height: 100vh;
        }

        .dashboard-header {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .header-icon {
          color: #3b82f6;
          flex-shrink: 0;
        }

        .dashboard-title {
          font-size: 2rem;
          font-weight: 700;
          color: #1a202c;
          margin: 0;
        }

        .dashboard-subtitle {
          color: #64748b;
          margin: 0;
          font-size: 0.95rem;
        }

        .header-actions {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
        }

        .refresh-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s ease;
        }

        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        /* Grille des statistiques */
        .stats-grid .stat-card {
          border: none;
          border-radius: 12px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .stats-grid .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
        }

        .stat-icon-wrapper {
          width: 60px;
          height: 60px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
        }

        .stat-icon {
          color: white;
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #000000;
          margin-bottom: 0.5rem;
        }

        .stat-label {
          color: #64748b;
          font-size: 0.9rem;
          font-weight: 500;
        }

        .stat-growth {
          margin-top: 0.5rem;
          font-size: 0.8rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.25rem;
        }

        .stat-growth.positive {
          color: #10b981;
        }

        .stat-growth.negative {
          color: #ef4444;
        }

        /* Actions rapides */
        .quick-actions .action-card {
          border: none;
          border-radius: 12px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .quick-actions .action-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
        }

        .action-icon {
          width: 60px;
          height: 60px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 0.75rem;
          color: white;
        }

        .action-label {
          font-weight: 600;
          color: #1a202c;
          font-size: 0.9rem;
        }

        /* Cartes du dashboard */
        .dashboard-card {
          border: none;
          border-radius: 12px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
        }

        .card-header-modern {
          background: white;
          border-bottom: 1px solid #e2e8f0;
          border-radius: 12px 12px 0 0 !important;
          padding: 1.25rem 1.5rem;
          font-weight: 600;
          color: #1a202c;
        }

        /* Styles pour les sections en mode 2x2 */
        .top-products,
        .stock-alerts,
        .credit-sales,
        .team-activity {
          padding: 0;
        }

        .product-item,
        .alert-item,
        .credit-item,
        .team-member {
          padding: 0.75rem 0;
          border-bottom: 1px solid #e9ecef;
        }

        .product-item:last-child,
        .alert-item:last-child,
        .credit-item:last-child,
        .team-member:last-child {
          border-bottom: none;
        }

        .product-rank {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          background: #3b82f6;
          color: white;
          border-radius: 50%;
          font-size: 0.8rem;
          font-weight: 700;
          margin-right: 0.75rem;
          flex-shrink: 0;
        }

        .product-info,
        .alert-info,
        .credit-info,
        .member-info {
          flex: 1;
        }

        .product-badge {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 0.25rem;
        }

        .sales-badge {
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
        }

        .credit-status {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 0.25rem;
        }

        .credit-time {
          font-size: 0.75rem;
          color: #64748b;
          font-weight: 500;
        }

        .product-name,
        .alert-name,
        .credit-customer,
        .member-name {
          font-weight: 600;
          color: #1a202c;
          margin-bottom: 0.25rem;
        }

        .product-stats,
        .alert-details,
        .credit-details,
        .member-stats {
          font-size: 0.85rem;
          color: #64748b;
        }

        /* États vides */
        .empty-state {
          text-align: center;
          padding: 2rem 1rem;
        }

        /* Media queries pour responsive */
        @media (max-width: 1200px) {
          .dashboard-container {
            padding: 1rem;
          }

          .dashboard-header {
            padding: 1.5rem;
          }

          .dashboard-title {
            font-size: 1.75rem;
          }
        }

        @media (max-width: 992px) {
          /* Sur tablette, les cartes passent en 1 colonne */
          .row .col-lg-6 {
            margin-bottom: 1rem;
          }
        }

        /* Media queries pour responsive */
        @media (max-width: 1200px) {
          .dashboard-container {
            padding: 1rem;
          }

          .dashboard-header {
            padding: 1.5rem;
          }

          .dashboard-title {
            font-size: 1.75rem;
          }
        }

        @media (max-width: 768px) {
          .dashboard-container {
            padding: 0.75rem;
          }

          .dashboard-header {
            padding: 1.25rem;
          }

          .header-content {
            flex-direction: column;
            text-align: center;
            gap: 0.75rem;
          }

          .dashboard-title {
            font-size: 1.5rem;
          }

          .stat-value {
            font-size: 1.2rem;
          }

          .stat-icon-wrapper,
          .action-icon {
            width: 50px;
            height: 50px;
          }

          .header-actions {
            justify-content: center;
            margin-top: 1rem;
          }
        }

        @media (max-width: 576px) {
          .dashboard-container {
            padding: 0.5rem;
          }

          .dashboard-header {
            padding: 1rem;
          }

          .dashboard-title {
            font-size: 1.25rem;
          }

          .stat-value {
            font-size: 1.25rem;
          }

          .product-rank {
            width: 28px;
            height: 28px;
            font-size: 0.75rem;
          }

          .product-item,
          .alert-item,
          .credit-item,
          .team-member {
            padding: 0.75rem;
          }

          .card-header-modern {
            padding: 1rem;
          }
        }
      `}</style>
    </Container>
  );
};

export default ManagerDashboard;